import { useEffect, useCallback, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import {
  useGetObservedLogsQuery,
  useAddObservedLogMutation,
  useRemoveObservedLogMutation,
} from "../store/slices/apiSlice";
import { Log } from "../store/types";
import { API_BASE_URL } from "../config";
import { checkSession } from "../auth/session";

type ObserverItem = {
  wellUid: string;
  wellboreUid: string;
  logUid: string;
};

type LogDataListener = (data: any) => void;
type NewLogListener = (log: Log) => void;

const listeners = new Map<string, Set<LogDataListener>>();
const newLogListeners = new Map<string, Set<NewLogListener>>();
// Ключ велбора -> сколько компонентов держат подписку на него.
const wellboreSubscriptions = new Map<string, number>();
let sharedConnection: signalR.HubConnection | null = null;
let startPromise: Promise<void> | null = null;
let reconnectTimeout: NodeJS.Timeout | null = null;
let activeHookCount = 0;

const makeWellboreKey = (wellUid: string, wellboreUid: string) =>
  `${wellUid}|${wellboreUid}`;

const splitWellboreKey = (key: string) => {
  const [wellUid, wellboreUid] = key.split("|");
  return { wellUid, wellboreUid };
};

export const isTreeLogActive = (log?: Log | null): boolean =>
  Boolean(log?.objectGrowing);

const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

// Сервер шлёт строки в logData; спецификации кривых приходят не всегда —
// тогда собираем их из списка мнемоник уведомления.
const toCurveSpecifications = (data: any) => {
  const specifications = data.logData?.curveSpecifications;
  if (Array.isArray(specifications) && specifications.length > 0) {
    return specifications;
  }

  const mnemonics: string[] | undefined = Array.isArray(data.mnemonics)
    ? data.mnemonics
    : data.logData?.mnemonicList?.split?.(",");

  return (mnemonics || []).map((mnemonic: string) => ({
    mnemonic: mnemonic.trim(),
    unit: "",
  }));
};

const notifyLogListeners = (data: any) => {
  const logUid = data.logHeader?.uid;
  if (!logUid) return;

  const logData = {
    curveSpecifications: toCurveSpecifications(data),
    data: data.logData?.data || [],
  };

  listeners.get(logUid)?.forEach((callback) => callback(logData));
};

const notifyNewLogListeners = (notification: any) => {
  const logHeader = notification?.logHeader as Log | undefined;
  const wellUid = notification?.wellUid ?? logHeader?.wellUid;
  const wellboreUid = notification?.wellboreUid ?? logHeader?.wellboreUid;
  if (!logHeader?.uid || !wellUid || !wellboreUid) return;

  newLogListeners
    .get(makeWellboreKey(wellUid, wellboreUid))
    ?.forEach((callback) => callback(logHeader));
};

// Подписка живёт только на текущем соединении: после реконнекта её нужно оформить заново.
const invokeHub = async (
  method: "Subscribe" | "Unsubscribe",
  wellUid: string,
  wellboreUid: string
) => {
  const connection = sharedConnection;
  if (connection?.state !== signalR.HubConnectionState.Connected) return;

  try {
    await connection.invoke(method, wellUid, wellboreUid);
  } catch {
    // Соединение могло оборваться прямо во время вызова — подписку восстановит onreconnected.
  }
};

const resubscribeAll = () => {
  wellboreSubscriptions.forEach((_, key) => {
    const { wellUid, wellboreUid } = splitWellboreKey(key);
    invokeHub("Subscribe", wellUid, wellboreUid);
  });
};

const createConnection = () => {
  const hubUrl = `${API_BASE_URL}/hubs/logs`;

  const connection = new signalR.HubConnectionBuilder()
    .withUrl(hubUrl, {
      skipNegotiation: true,
      transport: signalR.HttpTransportType.WebSockets,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  connection.on("LogDataReceived", notifyLogListeners);
  connection.on("NewLogReceived", notifyNewLogListeners);
  connection.onreconnected(resubscribeAll);
  // Обрыв может значить, что сервер завершил сессию: тогда не ждём конца попыток переподключения.
  connection.onreconnecting(() => checkSession());
  connection.onclose((error) => {
    startPromise = null;
    if (activeHookCount === 0) return;

    // Завершая сессию пользователя, сервер закрывает его соединения штатно — без ошибки
    // и без автопереподключения. Поэтому проверяем сессию при любом закрытии.
    checkSession();
    if (!error) return;

    clearReconnectTimeout();
    reconnectTimeout = setTimeout(connectSignalR, 3000);
  });

  return connection;
};

const connectSignalR = (): Promise<void> => {
  if (
    sharedConnection?.state === signalR.HubConnectionState.Connected ||
    sharedConnection?.state === signalR.HubConnectionState.Connecting ||
    sharedConnection?.state === signalR.HubConnectionState.Reconnecting
  ) {
    return startPromise ?? Promise.resolve();
  }

  if (!sharedConnection) {
    sharedConnection = createConnection();
  }

  clearReconnectTimeout();
  startPromise = sharedConnection
    .start()
    .then(resubscribeAll)
    .catch(() => {
      startPromise = null;
      checkSession();
      if (activeHookCount > 0) {
        reconnectTimeout = setTimeout(connectSignalR, 3000);
      }
    });

  return startPromise;
};

// Одно общее соединение на всё приложение: последний размонтированный потребитель его гасит.
const useSharedConnection = () => {
  useEffect(() => {
    activeHookCount += 1;
    connectSignalR().catch(() => undefined);

    return () => {
      activeHookCount = Math.max(0, activeHookCount - 1);
      if (activeHookCount === 0) {
        clearReconnectTimeout();
        listeners.clear();
        newLogListeners.clear();
        wellboreSubscriptions.clear();
        const connection = sharedConnection;
        sharedConnection = null;
        startPromise = null;
        connection?.stop().catch(() => undefined);
      }
    };
  }, []);
};

/**
 * Подписка на велбор: включает оба события хаба (LogDataReceived и NewLogReceived).
 * Пока подписки нет, сервер ничего не шлёт. Восстанавливается автоматически после реконнекта.
 */
export const useWellboreSubscription = (
  wellUid?: string | null,
  wellboreUid?: string | null,
  onNewLog?: NewLogListener
) => {
  useSharedConnection();

  const onNewLogRef = useRef(onNewLog);
  useEffect(() => {
    onNewLogRef.current = onNewLog;
  }, [onNewLog]);

  useEffect(() => {
    if (!wellUid || !wellboreUid) return;

    const key = makeWellboreKey(wellUid, wellboreUid);
    const notify: NewLogListener = (log) => onNewLogRef.current?.(log);

    if (!newLogListeners.has(key)) {
      newLogListeners.set(key, new Set());
    }
    newLogListeners.get(key)!.add(notify);

    const holders = wellboreSubscriptions.get(key) ?? 0;
    wellboreSubscriptions.set(key, holders + 1);
    if (holders === 0) {
      connectSignalR()
        .then(() => invokeHub("Subscribe", wellUid, wellboreUid))
        .catch(() => undefined);
    }

    return () => {
      newLogListeners.get(key)?.delete(notify);
      if (newLogListeners.get(key)?.size === 0) {
        newLogListeners.delete(key);
      }

      const rest = (wellboreSubscriptions.get(key) ?? 1) - 1;
      if (rest > 0) {
        wellboreSubscriptions.set(key, rest);
        return;
      }

      wellboreSubscriptions.delete(key);
      invokeHub("Unsubscribe", wellUid, wellboreUid);
    };
  }, [wellUid, wellboreUid]);
};

export const useLogObserver = () => {
  const { data: observedLogsRaw = [], refetch } = useGetObservedLogsQuery();
  const [addObserved] = useAddObservedLogMutation();
  const [removeObserved] = useRemoveObservedLogMutation();
  const observedLogs = observedLogsRaw as ObserverItem[];

  useSharedConnection();

  const toggleObserve = useCallback(
    async (wellUid: string, wellboreUid: string, logUid: string) => {
      const exists = observedLogs.some((item) => item.logUid === logUid);
      if (exists) {
        await removeObserved({ wellUid, wellboreUid, logUid });
      } else {
        await addObserved({ wellUid, wellboreUid, logUid });
      }
      await refetch();
    },
    [observedLogs, addObserved, removeObserved, refetch]
  );

  const isObserved = useCallback(
    (logUid: string) => observedLogs.some((item) => item.logUid === logUid),
    [observedLogs]
  );

  const hasObservedLog = useCallback(
    (wellboreUid: string) => {
      return observedLogs.some((item) => item.wellboreUid === wellboreUid);
    },
    [observedLogs]
  );

  const subscribe = useCallback(
    (logUid: string, onData: (data: any) => void) => {
      if (!listeners.has(logUid)) {
        listeners.set(logUid, new Set());
      }
      listeners.get(logUid)!.add(onData);
      return () => {
        listeners.get(logUid)?.delete(onData);
        if (listeners.get(logUid)?.size === 0) {
          listeners.delete(logUid);
        }
      };
    },
    []
  );

  return {
    observedLogs,
    isObserved,
    hasObservedLog,
    toggleObserve,
    subscribe,
  };
};
